import { WatchQueryFetchPolicy } from '@apollo/client';
import { ID } from 'graphql-ws';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshControl } from 'react-native';

import {
  ListProjectsQuery,
  Project,
  ProjectMemberRole,
  ProjectSortOption,
  useListProjectsQuery,
} from '@graphql/generated';
import { useGetUserAvatarsByIdsQuery } from '@hooks/useGetUserAvatarsByIdsQuery';
import {
  logError,
  LogGraphError,
  LogNetworkError,
} from '@utils/logNonFatalError';

/**
 * Retrieves a list of projects
 */
const DEFAULT_PER_PAGE_COUNT = 50;

interface ListProjectsFromQueryProps {
  userId?: string;
  sortBy?: ProjectSortOption;
  role?: ProjectMemberRole;
  archivesOnly?: boolean;
  locationOnly?: boolean;
  onCompleted?: () => void;
  onError?: () => void;
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  filterTeamsProjects?: boolean;
  ownerIds?: ID[];
  memberIds?: ID[];
  teamIds?: ID[];
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
  recentFirst?: number;
}

export const useListProjectsFromQuery = (
  props?: ListProjectsFromQueryProps
) => {
  const {
    userId = undefined,
    sortBy = undefined,
    role = undefined,
    filterTeamsProjects = false,
    archivesOnly = false,
    locationOnly = false,
    onCompleted = undefined,
    onError = undefined,
    first = DEFAULT_PER_PAGE_COUNT,
    last = undefined,
    after = undefined,
    before = undefined,
    ownerIds = [],
    memberIds = [],
    teamIds = [],
    skip = false,
    fetchPolicy = 'cache-first',
    recentFirst = undefined,
  } = props || {};

  const [refreshing, setRefreshing] = useState(false);
  const includeRecent = recentFirst !== undefined;

  const { data, loading, error, refetch, fetchMore } = useListProjectsQuery({
    variables: {
      userId,
      sortBy,
      role,
      archivesOnly,
      locationOnly,
      first,
      last,
      filterTeamsProjects,
      before,
      after,
      ownerIds: [...ownerIds].sort((a, b) => a.localeCompare(b)),
      memberIds: [...memberIds].sort((a, b) => a.localeCompare(b)),
      teamIds: [...teamIds].sort((a, b) => a.localeCompare(b)),
      recentFirst,
      includeRecent,
    },
    skip,
    fetchPolicy,
  });

  // Use refs so effects always call the latest callback without needing them
  // in the dependency array (avoids stale closures when callers pass inline fns).
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!loading) setRefreshing(false);
  }, [loading]);
  useEffect(() => {
    if (data) onCompletedRef.current?.();
  }, [data]);
  useEffect(() => {
    if (!error) return;
    if (error.networkError) {
      const event: LogNetworkError = {
        message: 'List Projects Network Failure',
        errorMessage: error.message,
        statusCode:
          'statusCode' in error.networkError
            ? error.networkError.statusCode
            : -1,
        extra: [{ archivesOnly }],
      };
      logError(event);
    }
    error.graphQLErrors.forEach((graphError) => {
      const event: LogGraphError = {
        message: 'List Projects GraphQL Error',
        errorMessage: graphError.message,
        operationName: 'listProjects',
        extra: [{ archivesOnly }],
      };
      logError(event);
    });
    onErrorRef.current?.();
  }, [error, archivesOnly]);

  const { checkUserAvatarURLByIds } = useGetUserAvatarsByIdsQuery();
  // Ref so the effect always calls the latest instance without depending on it
  // (the hook returns a new function each render).
  const checkUserAvatarURLByIdsRef = useRef(checkUserAvatarURLByIds);
  checkUserAvatarURLByIdsRef.current = checkUserAvatarURLByIds;

  // Validate the creator avatars shown on project rows. Keyed on the derived,
  // sorted set of avatar IDs (not edges?.length) so a replaced project/creator
  // re-triggers the check even when the count is unchanged. Covers both the
  // main list and the recent-projects section. (TA-9793)
  const projectAvatarIdsKey = useMemo(() => {
    const avatarIds = new Set<string>();
    [
      ...(data?.allProjects?.edges ?? []),
      ...(data?.recentProjects?.edges ?? []),
    ].forEach((edge) => {
      const id = edge?.node?.creator?.avatar?.id;
      if (id) avatarIds.add(id);
    });
    return [...avatarIds].sort((a, b) => a.localeCompare(b)).join(',');
  }, [data?.allProjects?.edges, data?.recentProjects?.edges]);

  useEffect(() => {
    if (!projectAvatarIdsKey) return;
    checkUserAvatarURLByIdsRef.current(projectAvatarIdsKey.split(','));
  }, [projectAvatarIdsKey]);

  const refreshControl = (
    <RefreshControl
      refreshing={refreshing}
      onRefresh={() => {
        setRefreshing(true);
        refetch()
          .catch(() => {})
          .finally(() => setRefreshing(false));
      }}
    />
  );

  const fetchFromCursor = () => {
    if (!projects || !pageInfo?.hasNextPage || loading) return;

    const cursor = pageInfo.endCursor;
    fetchMore({
      variables: {
        after: cursor,
        first,
        includeRecent: false,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const newEdges = fetchMoreResult.allProjects?.edges ?? [];
        const nextPageInfo = fetchMoreResult.allProjects?.pageInfo;
        const previousEdges = previousResult.allProjects?.edges || [];

        // Combined and de-duplicate by project node.id
        const allEdges = [...previousEdges, ...newEdges];
        const uniqueEdgesMap = new Map();

        allEdges.forEach((edge) => {
          if (edge?.node?.id) {
            uniqueEdgesMap.set(edge.node.id, edge);
          }
        });

        const uniqueEdges = Array.from(uniqueEdgesMap.values());

        return (
          newEdges.length
            ? {
                ...previousResult,
                allProjects: {
                  __typename: previousResult.allProjects?.__typename,
                  edges: uniqueEdges,
                  pageInfo: nextPageInfo,
                },
              }
            : previousResult
        ) as ListProjectsQuery;
      },
    });
  };

  const { allProjects, recentProjects: recentProjectsConnection } = data || {
    allProjects: undefined,
    recentProjects: undefined,
  };
  const { edges = [], pageInfo } = allProjects || { edges: [] };
  const projects =
    edges?.reduce((acc, curr) => {
      if (curr?.node !== undefined) {
        return [...acc, curr.node as Project];
      } else {
        return acc;
      }
    }, [] as Project[]) || [];
  const recentProjects =
    recentProjectsConnection?.edges?.reduce((acc, curr) => {
      if (curr?.node !== undefined) {
        return [...acc, curr.node as Project];
      } else {
        return acc;
      }
    }, [] as Project[]) || [];

  return {
    projects,
    recentProjects,
    loading,
    error,
    pageInfo,
    refetch,
    fetchFromCursor,
    refreshing,
    setRefreshing,
    refreshControl,
  };
};
